import { useStaleData } from '@timmons-group/shared-react-components/hooks';
import { FIELD_TYPES } from '@timmons-group/shared-react-components/constants';
import { useEffect, useState } from 'react';

import { VALIDATIONS, RENDER_VALIDATIONS } from 'constants';
import { createFieldValidation } from 'formHelpers';

const validationTypes = Object.values(VALIDATIONS);
const layoutValidationTypes = Object.values(RENDER_VALIDATIONS);

export const useLayout = (type, key) => {
  const url = `/api/layout/get?objectType=${type}&layoutKey=${key}`;

  const [data, isLoading] = useStaleData(url, {});
  const [parsedLayout, setParsedLayout] = useState(null);
  const [isParsing, setIsParsing] = useState(true);


  useEffect(() => {
    if (!isLoading && data) {
      setParsedLayout(parseLayout(data));
      setIsParsing(false);
    }
  }, [data, isLoading]);

  return [data, isParsing, parsedLayout];
}

export const parseLayout = (layout) => {
  if (!layout) {
    return {};
  }

  const sections = [];
  const triggerFields = new Map();
  const fields = new Map();

  if (layout.sections?.length) {
    layout.sections.forEach((section) => {
      sections.push(parseSection(section, fields, triggerFields));
    });
  }

  // Create validations for each field that has conditional validations
  triggerFields.forEach((trigField, key) => {
    trigField.fieldValues.forEach((fieldValues, value) => {
      fieldValues.forEach((triggeredValidations, aFI) => {
        const layout = new Map();
        const validationProps = new Map();
        triggeredValidations.forEach((validation) => {

          // loop through validation object
          Object.keys(validation).forEach((key) => {
            // Setting layout validations like "required" or "disabled" that must also be visually represented
            if (layoutValidationTypes.includes(key)) {
              layout.set(key, validation[key]);
            }

            // Setting the actual validation props
            if (validationTypes.includes(key)) {
              validationProps.set(key, validation[key]);
            }
          });
        });

        const {type, label} = fields.get(aFI);
        // Create a yup validation for the field that is triggered by the triggerField
        fieldValues.set(aFI, {layout, validation: createFieldValidation(type, label, validationProps)});
      });
    });
  });

  return { sections, fields, triggerFields };
}

export const parseSection = (section, fieldMap, triggerFieldMap) => {
  if (!section) {
    return {};
  }
  const { layout, editable, enabled } = section;

  const parsedSection = {
    name: section.name,
    editable,
    enabled,
    fields: [],
  };

  if (layout?.length) {
    layout.forEach((field) => {
      const parsedField = parseField(field);
      if (parsedField) {
        fieldMap.set(field.path, parsedField);
        parsedSection.fields.push(field.path);
        const { conditions } = parsedField;
        if (conditions.length) {
          parseConditions(field.path, triggerFieldMap, conditions);
        }
      }
    });
  }

  return parsedSection;
};

export const parseField = (field) => {
  if (!field) {
    return {};
  }

  const { label, type, model, hidden = false, conditions = [], linkFormat } = field;
  const name = model.name || `unknown${model.id}`;
  const required = !!field.required;
  const readOnly = !!field.readOnly;
  const disabled = !!field.disabled;
  const defaultValue = field.defaultValue;
  const containsDefaultValue = !!field.defaultValue;

  const parsedField = {
    id: name,
    conditions,
    label,
    type,
    hidden,
    render: {
      type: type,
      label,
      name,
      hidden,
      required,
      disabled,
      readOnly,
      linkFormat,
    }
  }

  if (containsDefaultValue)
    parsedField.render.defaultValue = defaultValue;

  const { data = {} } = model || {};

  const types = Object.keys(FIELD_TYPES);
  if (!types[type]) {
    console.warn(`Field type ${type} is not supported`);
    return parsedField;
  }

  if (type === FIELD_TYPES.SELECT || type === FIELD_TYPES.MULTI_SELECT) {
    parsedField.options = field.possibleChoices;
  }

  const validations = new Map();
  parseValidation(validations, field);
  parseValidation(validations, data);

  if (validations.size) {
    parsedField.validations = createFieldValidation(type, label, validations);
  }

  return parsedField;
}

const parseValidation = (validationMap, data) => {
  Object.keys(data).forEach((key) => {
    if (validationTypes.includes(key)) {
      validationMap.set(key, data[key]);
    }
  });
}

const parseConditions = (fieldId, triggerFields, conditions) => {
  if (conditions?.length) {
    conditions.forEach((condition) => {
      const { is: value, when: field, then: validations } = condition;

      // touches is a map of every field that triggerfield could influence.
      // For any value a triggerField fires we need to roll back any fields that COULD have been affected by previous values
      const trigField = triggerFields.get(field) || { id: field, fieldValues: new Map(), touches: new Map() };

      const fieldValues = trigField.fieldValues.get(value) || new Map();
      const affectedFields = fieldValues.get(fieldId) || [];
      const touched = trigField.touches.get(fieldId) || new Map();
      touched.set(value, true);
      trigField.touches.set(fieldId, touched);
      affectedFields.push(validations);
      fieldValues.set(fieldId, affectedFields);
      trigField.fieldValues.set(value, fieldValues);

      triggerFields.set(field, trigField);
    });
  }

  // return triggerFields;
}
